import type { POForecastData } from '@/types/po'
import { useT } from '@/i18n/useT'

interface OverviewChartProps {
  data: POForecastData
  lineType: 'linear' | 'monotone' | 'step' | 'stepAfter'
}

interface ChartPoint {
  x: number
  name: string
  actualCumulative: number | null
  forecastCumulative: number | null
}

interface InvoiceDot {
  x: number
  y: number
  amount: number
  date: string
}

function clampMonth(value: number): number {
  return Math.min(Math.max(value, 1), 12)
}

export function buildStepChartData(data: POForecastData): ChartPoint[] {
  return data.monthlyData.map((month) => {
    // Keep the actual line continuous so months without bookings still render as plateaus.
    const actualCumulative = month.cumulativeActual

    // Keep forecast continuous from the forecast start onward, including months with booked invoices.
    const forecastStartsAt = data.currentMonth > 0 ? data.currentMonth : 1
    const forecastCumulative =
      data.isPastYear || month.month < forecastStartsAt ? null : month.cumulativeForecast

    return {
      x: month.month,
      name: month.monthName,
      actualCumulative,
      forecastCumulative,
    }
  })
}

export function buildInvoiceDots(data: POForecastData): InvoiceDot[] {
  return (data.invoiceMarkers || []).map((marker) => {
    const month = clampMonth(Math.floor(marker.monthPosition))
    const monthData = data.monthlyData[month - 1]

    return {
      x: marker.monthPosition,
      y: monthData?.cumulativeForecast ?? 0,
      amount: marker.amount,
      date: marker.date,
    }
  })
}

/* --- SVG geometry, matching the design mockup's 1000×240 viewBox --- */
const W = 1000
const H = 240
const PAD_L = 14
const PAD_R = 14
const PAD_T = 16
const PAD_B = 12

const STEP_CHOICES = [5e3, 1e4, 2e4, 25e3, 5e4, 1e5, 2e5, 25e4, 5e5, 1e6]

/** Left gutter reserved for the y-axis labels. */
const AXIS_GUTTER = 82

/** Rounds the axis up to a whole number of ticks, at most 7 of them. */
function axisMax(rawMax: number): { maxY: number; step: number } {
  const step = STEP_CHOICES.find((s) => rawMax / s <= 7) ?? 1e6
  return { maxY: Math.max(Math.ceil(rawMax / step) * step, step), step }
}

function compactEur(value: number): string {
  return `${new Intl.NumberFormat('de-DE', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)} €`
}

function formatEUR(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)
}

/**
 * Builds the path through a series of points, honouring the line-type the user
 * picked: straight segments, a stepped staircase, or a Catmull-Rom smoothing.
 */
function buildPath(points: { x: number; y: number }[], lineType: OverviewChartProps['lineType']): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`

  const head = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`

  if (lineType === 'step' || lineType === 'stepAfter') {
    const segments = points.slice(1).map((p, i) => {
      const prev = points[i]
      // stepAfter holds the previous level until the new x; step rises at the midpoint
      const turnX = lineType === 'stepAfter' ? p.x : (prev.x + p.x) / 2
      return `H${turnX.toFixed(1)} V${p.y.toFixed(1)} H${p.x.toFixed(1)}`
    })
    return `${head} ${segments.join(' ')}`
  }

  if (lineType === 'monotone') {
    const segments: string[] = []
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] ?? points[i]
      const p1 = points[i]
      const p2 = points[i + 1]
      const p3 = points[i + 2] ?? p2
      const c1x = p1.x + (p2.x - p0.x) / 6
      const c1y = p1.y + (p2.y - p0.y) / 6
      const c2x = p2.x - (p3.x - p1.x) / 6
      const c2y = p2.y - (p3.y - p1.y) / 6
      segments.push(
        `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`,
      )
    }
    return `${head} ${segments.join(' ')}`
  }

  return `${head} ${points.slice(1).map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}`
}

/**
 * Cumulative spend against the PO budget, drawn as the design does it: a hand
 * -rolled SVG on the token palette, with the legend and the totals in a side
 * rail rather than a chart legend.
 */
export function OverviewChart({ data, lineType }: OverviewChartProps) {
  const { t } = useT()
  const chartData = buildStepChartData(data)
  const invoiceDots = buildInvoiceDots(data)

  const maxCumulative =
    data.monthlyData.length > 0
      ? Math.max(...data.monthlyData.map((m) => m.cumulativeForecast))
      : 0
  const { maxY, step } = axisMax(Math.max(maxCumulative, data.totalPot, data.totalForecast, 1))

  const x = (month: number) => PAD_L + ((month - 1) / 11) * (W - PAD_L - PAD_R)
  const y = (value: number) => H - PAD_B - (value / maxY) * (H - PAD_B - PAD_T)

  const tickCount = Math.round(maxY / step)
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const value = i * step
    return {
      value,
      y: y(value),
      top: `${((y(value) - 0) / H) * 100}%`,
      label: value === 0 ? '0' : compactEur(value),
    }
  })

  // The actual line stops at the current month; beyond that only a forecast exists
  const actualUntil = data.isPastYear ? 12 : clampMonth(data.currentMonth || 1)
  const actualPoints = chartData
    .filter((p) => p.x <= actualUntil && p.actualCumulative != null)
    .map((p) => ({ x: x(p.x), y: y(p.actualCumulative as number) }))

  const forecastPoints = chartData
    .filter((p) => p.forecastCumulative != null)
    .map((p) => ({ x: x(p.x), y: y(p.forecastCumulative as number) }))

  const actualPath = buildPath(actualPoints, lineType)
  const forecastPath = buildPath(forecastPoints, lineType)

  const areaPath =
    actualPoints.length > 1
      ? `${actualPath} L${actualPoints[actualPoints.length - 1].x.toFixed(1)},${y(0).toFixed(1)} L${actualPoints[0].x.toFixed(1)},${y(0).toFixed(1)} Z`
      : ''

  const budgetY = y(data.totalPot)
  const delta = data.totalForecast - data.totalPot

  return (
    <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 420px', minWidth: 0 }}>
        <div style={{ position: 'relative', marginTop: 18, paddingLeft: AXIS_GUTTER }}>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '1000 / 240' }}>
            {yTicks.map((t) => (
              <div
                key={t.value}
                style={{
                  position: 'absolute',
                  left: -AXIS_GUTTER,
                  width: AXIS_GUTTER - 10,
                  textAlign: 'right',
                  fontSize: 11,
                  color: 'var(--faint)',
                  // German labels like "800.000 €" are wider than the mockup's
                  // "412K" placeholders and must not wrap onto two lines.
                  whiteSpace: 'nowrap',
                  transform: 'translateY(-50%)',
                  top: t.top,
                }}
              >
                {t.label}
              </div>
            ))}
            <div
              style={{
                position: 'absolute',
                left: 2,
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--faint)',
                background: 'var(--paper)',
                padding: '0 5px',
                transform: 'translateY(-140%)',
                top: `${(budgetY / H) * 100}%`,
                whiteSpace: 'nowrap',
              }}
            >
              {t('Budget')} {compactEur(data.totalPot)}
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%', display: 'block' }}>
              {yTicks.map((t) => (
                <line
                  key={t.value}
                  x1={PAD_L}
                  x2={W - PAD_R}
                  y1={t.y}
                  y2={t.y}
                  stroke="var(--line2)"
                  strokeWidth={1}
                />
              ))}
              {areaPath && <path d={areaPath} fill="var(--accentSoft)" stroke="none" />}
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={budgetY}
                y2={budgetY}
                stroke="var(--muted)"
                strokeWidth={1}
              />
              <path
                d={actualPath}
                fill="none"
                stroke="var(--accent)"
                strokeWidth={1.6}
                strokeLinejoin="round"
                data-testid="actual-line"
                data-line-type={lineType}
              />
              <path
                d={forecastPath}
                fill="none"
                stroke={data.isExceeded ? 'var(--err)' : 'var(--muted)'}
                strokeWidth={1.4}
                strokeDasharray="2 5"
                strokeLinecap="round"
                strokeLinejoin="round"
                data-testid="forecast-line"
                data-line-type={lineType}
              />
              {chartData.map((p) => (
                <line
                  key={p.x}
                  x1={x(p.x)}
                  x2={x(p.x)}
                  y1={H - PAD_B}
                  y2={H - PAD_B + 4}
                  stroke="var(--line)"
                  strokeWidth={1}
                />
              ))}
              {invoiceDots.map((dot) => (
                <circle
                  key={`${dot.date}-${dot.amount}-${dot.x}`}
                  cx={x(clampMonth(dot.x))}
                  cy={y(dot.y)}
                  r={2.4}
                  fill="var(--accent)"
                  data-testid="invoice-dot"
                >
                  <title>{`${dot.date}: ${formatEUR(dot.amount)}`}</title>
                </circle>
              ))}
            </svg>
          </div>

          <div style={{ position: 'relative', height: 16, marginTop: 8 }}>
            {chartData.map((p) => (
              <div
                key={p.x}
                style={{
                  position: 'absolute',
                  transform: 'translateX(-50%)',
                  fontSize: 10,
                  color: 'var(--faint)',
                  left: `${(x(p.x) / W) * 100}%`,
                }}
              >
                {/* The API sends Jan…Dec, the same keys the dashboard chart uses */}
                {t(p.name)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          flexShrink: 0,
          alignSelf: 'stretch',
          borderLeft: '1px solid var(--line2)',
          paddingLeft: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--faint)' }}>
            <span style={{ width: 16, height: 2, background: 'var(--accent)', flexShrink: 0 }} />
            {t('Invoiced')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--faint)' }}>
            <span
              style={{
                width: 16,
                height: 0,
                borderTop: `2px dotted ${data.isExceeded ? 'var(--err)' : 'var(--muted)'}`,
                flexShrink: 0,
              }}
            />
            {t('Forecast')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--faint)' }}>
            <span style={{ width: 16, height: 0, borderTop: '1px solid var(--muted)', flexShrink: 0 }} />
            {t('Budget')}
          </div>
        </div>

        <div style={{ height: 1, background: 'var(--line2)' }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--faint)' }}>{t('Total PO budget')}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
              {formatEUR(data.totalPot)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--faint)' }}>{t('Invoiced to date')}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2, color: 'var(--ok)' }}>
              {formatEUR(data.totalUsed)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--faint)' }}>{t('Forecast year end')}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
              {formatEUR(data.totalForecast)}
            </div>
          </div>
          <div>
            {/* Deliberately not just "Overrun": the KPI tile above uses that
                word for the sum of per-PO overruns, which is a different
                number from forecast-minus-budget. */}
            <div style={{ fontSize: 11, color: 'var(--faint)' }}>
              {data.isExceeded ? t('Forecast over budget') : t('Remaining forecast')}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                marginTop: 2,
                color: data.isExceeded ? 'var(--err)' : 'var(--text)',
              }}
            >
              {formatEUR(Math.abs(delta))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
